import React, { useEffect, useState } from "react";
import DynamicForm from "../../../Components/DynamicForm";
import { useUser } from "../../../UserContext";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
} from "@mui/material";
import SlidingSidebar, {
  ButtonControls,
  SidebarEditForm,
  SidebarAvatar,
} from "../../../Components/SlidingSidebar";

// Define constants for professional fields
const PROFESSIONAL_FIELDS = [
  "agency_name",
  "bio",
  "certifications",
  "languages",
];

interface CountyChip {
  county: string;
  state_code: string;
}

interface ChecklistChip {
  item: string;
}

interface FieldConfig {
  name: string;
  label: string;
  value: string | ChecklistItem[] | County[];
  fieldType?: string;
  chipFormat?:
    | ((chip: CountyChip) => string)
    | ((chip: ChecklistChip) => string);
  render?: (value: any) => React.ReactNode;
  onDelete?: (deletedChip: ChecklistItem) => void;
}

interface FormField {
  label: string;
  value:
    | string
    | number
    | Array<{ label: string; value: string | number }>
    | null;
  field: string;
  type?: "text" | "select" | "autocomplete" | "multiselect";
  options?: Array<{ label: string; value: string | number }>;
  disabled?: boolean;
  required?: boolean;
  onChange?: (value: any) => void;
}

// Add an interface for the county type
interface County {
  id: number;
  county: string;
  state_code: string;
  county_id?: number;
}

interface ChecklistItem {
  id: number;
  item: string;
  available: boolean; // Assuming you have this field based on your previous context
}

// Add this new interface
interface ChecklistPreset {
  id: number;
  items: ChecklistItem[]; // Assuming ChecklistItem is already defined
  // Add other properties if needed
}

// Username utilities
const fetchUsername = async (
  currentUser: any,
  roleDataId: number | undefined
) => {
  if (currentUser?.user_id && roleDataId !== undefined) {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/profiles/profile_profiles`,
      {
        params: {
          user_id: currentUser.role === "BA" ? currentUser.user_id : undefined,
          account_id:
            currentUser.role === "Account" || currentUser.role === "Owner"
              ? roleDataId
              : undefined,
          brand_id:
            currentUser.role === "Brand" || currentUser.role === "Supplier"
              ? roleDataId
              : undefined,
        },
      }
    );
    return response.data.length > 0 ? response.data[0] : null;
  }
  return null;
};

const checkUsernameAvailability = async (newUsername: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/profiles/check-profile`,
      {
        params: { username: newUsername },
      }
    );
    return response.data.length === 0 ? null : response.data[0].id;
  } catch (error) {
    console.error("Error checking username availability:", error);
    return null;
  }
};

const saveUsername = async (
  username: string,
  id: number | null,
  roleDataId: number,
  userRole: string
) => {
  try {
    if (id === null) {
      console.log("Creating new username with data:", {
        username,
        account_id: roleDataId,
        brand_id: roleDataId,
        user_id: roleDataId,
        type: userRole,
      });

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/profiles/profile_profiles`,
        {
          username,
          account_id: roleDataId,
          brand_id: roleDataId,
          user_id: roleDataId,
          type: userRole,
        }
      );

      console.log("Username creation response:", response.data);
    } else {
      console.log("Updating existing username");
      await axios.put(
        `${import.meta.env.VITE_API_URL}/profiles/profile_profiles/${id}`,
        {
          username,
          account_id: roleDataId,
          brand_id: roleDataId,
          user_id: roleDataId,
        }
      );
    }
  } catch (error) {
    console.error("Error saving username:", error);
    throw error;
  }
};

// Counties utilities
const getStates = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/util/states`
    );

    // Get the states array from the nested data structure
    const statesArray = response.data[0]?.data || [];

    // Map the states to the format needed by the form
    return statesArray.map((state: any) => ({
      label: state.state,
      value: state.state_code,
    }));
  } catch (error) {
    console.error("Error fetching states:", error);
    return [];
  }
};

const getCounties = async (state: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/ambassadors/ba-counties/${state}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching counties:", error);
    return [];
  }
};

const BASettings: React.FC = () => {
  const { currentUser, handleUserSelect } = useUser();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [professionalData, setProfessionalData] = useState<any>({});
  const [username, setUsername] = useState("");
  const [profileId, setProfileId] = useState<number | null>(null);
  const [roleDataId, setRoleDataId] = useState<number | undefined>(undefined);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<any>(null);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [availableCounties, setAvailableCounties] = useState<any[]>([]);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [noCountiesDialogOpen, setNoCountiesDialogOpen] = useState(false);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [selectedChecklistItems, setSelectedChecklistItems] = useState<
    ChecklistItem[]
  >([]);
  const [newItem, setNewItem] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  // Load professional data
  useEffect(() => {
    if (currentUser?.BAs_json && Array.isArray(currentUser.BAs_json)) {
      const data = currentUser.BAs_json[0] || {};

      const formattedCounties =
        data.ba_counties?.map((county: any) => ({
          id: county.county_id || county.id,
          county: county.county,
          state_code: county.state_code,
        })) || [];

      const formattedData = {
        ...data,
        username: username,
        ba_counties: formattedCounties,
        avatar_url: currentUser.avatar_url?.[0]?.avatar_url || "",
      };

      setProfessionalData(formattedData);

      if (editMode) {
        setEditData(formattedData);
      }

      if (formattedCounties.length > 0) {
        const uniqueStates = Array.from(
          new Set(formattedCounties.map((county: County) => county.state_code))
        ) as string[];
        setSelectedStates(uniqueStates);
        handleStateChange(uniqueStates);
      }
    }
  }, [currentUser, editMode, username]);

  // Load username
  useEffect(() => {
    const loadUsername = async () => {
      const newRoleDataId = currentUser?.user_id;
      setRoleDataId(newRoleDataId ?? undefined);
      const userProfile = await fetchUsername(
        currentUser,
        newRoleDataId ?? undefined
      );
      if (userProfile) {
        setUsername(userProfile.username);
        setProfileId(userProfile.id);
        setProfessionalData((prev: Record<string, any>) => ({
          ...prev,
          username: userProfile.username,
        }));
      }
    };
    loadUsername();
  }, [currentUser]);

  // Load states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesData = await getStates();
        setStateOptions(statesData);
      } catch (error) {
        // Handle error silently or show user feedback if needed
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (
    states: Array<string | { label: string; value: string }>
  ) => {
    const stateCodes = states.map((state) =>
      typeof state === "string" ? state : state.value
    );

    setSelectedStates(stateCodes);

    if (stateCodes.length > 0) {
      try {
        const countyPromises = stateCodes.map((state) => getCounties(state));
        const results = await Promise.all(countyPromises);
        const allCounties = results.flat();
        setAvailableCounties(allCounties);
      } catch (error) {
        setAvailableCounties([]);
      }
    } else {
      setAvailableCounties([]);
    }
  };

  const handleProfessionalUpdate = async (fieldName: string, value: string) => {
    if (!currentUser?.user_id || currentUser.role !== "BA") {
      console.error(
        "Current user is not defined, not a BA, or user_id is missing."
      );
      return;
    }

    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/ambassadors/ambassador-profile/${
          currentUser.user_id
        }`,
        {
          fieldName,
          value: value ?? null,
        }
      );

      // Refresh user data
      await handleUserSelect(currentUser.user_id);

      setSnackbarMessage("Professional information updated successfully.");
    } catch (error) {
      console.error("Error updating professional data:", error);
      setSnackbarMessage("Failed to update professional information.");
      throw error;
    }
  };

  const handleAvatarUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/ambassadors/ba-avatar`,
        formData,
        {
          params: { userId: currentUser?.user_id },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const newAvatarUrl = response.data.avatar_url;

      // Update both the professional data and edit form data with new avatar URL
      setProfessionalData((prev: Record<string, any>) => ({
        ...prev,
        avatar_url: newAvatarUrl,
      }));

      setEditData((prev: Record<string, any>) => ({
        ...prev,
        avatar_url: newAvatarUrl,
      }));
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setSnackbarMessage("Failed to upload avatar");
      setSnackbarOpen(true);
    }
  };

  const handleChecklistChange = (selected: any[]) => {
    console.log("selected test", selected);
    // Remove immediate API call, will be done on save instead
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewItem(""); // Reset the input field
  };

  const handleAddItem = () => {
    if (newItem) {
      // Create a temporary ID (negative to avoid conflicts)
      const tempId = -Date.now();

      // Add new item to the list
      const newItemObject = {
        id: tempId,
        item: newItem,
        available: true,
        type: "custom",
      };

      setChecklistItems((prev) => [...prev, newItemObject]);
      setSelectedChecklistItems((prev) => [...prev, newItemObject]);

      handleDialogClose();
    }
  };

  const saveNewChecklistItems = async () => {
    // Find items with temporary IDs (negative numbers)
    const newItems = selectedChecklistItems.filter((item) => item.id < 0);

    if (newItems.length > 0) {
      try {
        // Create new items first
        const promises = newItems.map((item) =>
          axios.post(
            `${import.meta.env.VITE_API_URL}/checklist/checklist_presets/${
              currentUser?.user_id ?? 0
            }`,
            {
              item: item.item,
              type: "custom",
              available: true,
              role: currentUser?.role,
              dynamic_id: currentUser?.user_id,
            }
          )
        );

        await Promise.all(promises);

        // Refresh the checklist items to get server-assigned IDs
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/checklist/checklist_presets/${
            currentUser?.user_id ?? 0
          }`
        );

        const allItems = response.data.flatMap(
          (checklist: any) => checklist.items
        );
        setChecklistItems(
          allItems.filter((item: ChecklistItem) => item.available)
        ); // Filter only available items
        console.log("checklistItems", allItems);
        setSelectedChecklistItems(
          allItems.filter((item: ChecklistItem) => item.available)
        );
      } catch (error) {
        console.error("Error adding new checklist items:", error);
        throw error;
      }
    }
  };

  const handleSaveProfessional = async () => {
    try {
      // Check if no counties are selected
      const selectedCounties = editData.ba_counties || [];
      if (selectedCounties.length === 0) {
        setNoCountiesDialogOpen(true);
        return;
      }

      // Save any new checklist items first
      await saveNewChecklistItems();

      await saveChanges();
    } catch (error: any) {
      console.error("Error saving professional data:", error);
      setSnackbarMessage(
        error.response?.data?.error || "Error updating professional information"
      );
      setSnackbarOpen(true);
    }
  };

  const saveChanges = async () => {
    try {
      // Handle username separately
      if (editData.username !== professionalData.username) {
        const id = await checkUsernameAvailability(editData.username);
        if (id !== null && id !== profileId) {
          setSnackbarMessage("Username is not available");
          setSnackbarOpen(true);
          return;
        }
        await saveUsername(
          editData.username,
          profileId,
          roleDataId!,
          currentUser?.role || "BA"
        );
      }

      // Sync counties changes in a single call
      const newCountyIds = (editData.ba_counties || [])
        .map((county: any) => county.id)
        .filter((id: number | null) => id != null); // Filter out any null values

      try {
        await axios.put(
          `${import.meta.env.VITE_API_URL}/ambassadors/ba-counties/${
            currentUser!.user_id
          }/sync`,
          { countyIds: newCountyIds }
        );
      } catch (error: any) {
        console.error("County sync failed:", {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status,
          counties: newCountyIds,
        });
        throw error; // Re-throw to be caught by outer catch
      }

      try {
        // Get the IDs of selected checklist items
        const selectedItemIds = selectedChecklistItems.map((item) => item.id);

        // Re-enable the checklist update with correct parameters
        await axios.put(
          `${import.meta.env.VITE_API_URL}/checklist/checklist_presets/${
            currentUser?.user_id ?? 0
          }`,
          {
            role: currentUser?.role,
            availableItemIds: selectedItemIds,
          }
        );

        console.log("Checklist items updated successfully");
      } catch (error: any) {
        console.error("Checklist update failed:", {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        // Don't throw the error to allow the rest of the form to work
        console.error("Continuing despite checklist error");
      }

      // Handle other professional fields
      for (const field of PROFESSIONAL_FIELDS) {
        if (editData[field] !== professionalData[field]) {
          await handleProfessionalUpdate(field, editData[field]);
        }
      }

      setSnackbarMessage("Professional information updated successfully");
      setSnackbarOpen(true);
      setEditMode(false);

      // Refresh user data
      if (currentUser) {
        await handleUserSelect(currentUser.user_id);
      }
    } catch (error: any) {
      console.error("Error saving professional data:", error);
      setSnackbarMessage(
        error.response?.data?.error || "Error updating professional information"
      );
      setSnackbarOpen(true);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setEditData({
      ...professionalData,
      username: username, // Explicitly include username
    });
  };

  useEffect(() => {
    const fetchChecklistPresets = async () => {
      console.log(currentUser?.user_id);
      try {
        const response = await axios.get<ChecklistPreset[]>(
          `${import.meta.env.VITE_API_URL}/checklist/checklist_presets/${
            currentUser?.user_id ?? 0
          }`
        );
        // Flatten the items from each checklist preset into a single array
        const allItems = response.data.flatMap((checklist) => checklist.items);
        setChecklistItems(
          allItems.filter((item: ChecklistItem) => item.available)
        ); // Filter only available items
        console.log("checklistItems", allItems);
        setSelectedChecklistItems(
          allItems.filter((item: ChecklistItem) => item.available)
        );
      } catch (error) {
        console.error("Error fetching checklist presets:", error);
      }
    };

    fetchChecklistPresets();
  }, [currentUser]);

  const getProfessionalFields = (): FieldConfig[] => {
    const avatarField: FieldConfig = {
      name: "avatar",
      label: "Avatar",
      value: currentUser?.avatar_url?.[0]?.avatar_url || "",
      fieldType: "avatar",
    };

    const usernameField: FieldConfig = {
      name: "username",
      label: "Username",
      value: username,
      fieldType:
        currentUser?.user_access?.user_status?.Admin !== "Yes"
          ? "RO"
          : undefined,
    };

    const baCountiesField: FieldConfig = {
      name: "ba_counties",
      label: "Coverage",
      fieldType: "chip",
      value: professionalData.ba_counties || [],
      chipFormat: (chip: CountyChip) => `${chip.county}, ${chip.state_code}`,
      render: (value: any) => {
        const counties = Array.isArray(value) ? value : [];
        return counties.length === 0 ? <em>No counties selected</em> : null;
      },
    };

    const checklistItemsField: FieldConfig = {
      name: "checklist_items",
      label: "Available Items",
      fieldType: "chip",
      value: selectedChecklistItems || [],
      chipFormat: (chip: ChecklistChip) => chip.item,
      render: (value: any) => {
        const items = Array.isArray(value) ? value : [];
        return items.length === 0 ? <em>No items selected</em> : null;
      },
      onDelete: (deletedChip: ChecklistItem) => {
        setSelectedChecklistItems((prev) =>
          prev.filter((item) => item.id !== deletedChip.id)
        );
      },
    };

    return [
      avatarField,
      usernameField,
      baCountiesField,
      checklistItemsField,
      ...PROFESSIONAL_FIELDS.map((field) => ({
        name: field,
        label: field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        value: professionalData[field] || "",
        fieldType:
          currentUser?.user_access?.user_status?.Admin !== "Yes"
            ? "RO"
            : undefined,
      })),
    ];
  };

  const getProfessionalFormSections = () => {
    const sections = [
      {
        title: "Profile Information",
        fields: [
          {
            label: "Username",
            value: editData?.username || "",
            field: "username",
            type: "text",
            required: true,
          } as FormField,
        ],
      },
      {
        title: "Professional Details",
        fields: [
          {
            label: "Agency Name",
            value: editData?.agency_name || "",
            field: "agency_name",
            type: "text",
            required: true,
          },
          {
            label: "Bio",
            value: editData?.bio || "",
            field: "bio",
            type: "text",
            required: true,
          },
          {
            label: "Certifications",
            value: editData?.certifications || "",
            field: "certifications",
            type: "text",
            required: false,
          },
          {
            label: "Languages",
            value: editData?.languages || "",
            field: "languages",
            type: "text",
            required: false,
          },
        ] as FormField[],
      },
      {
        title: "Market Coverage",
        fields: [
          {
            label: "Step 1: Select State(s)",
            field: "state",
            type: "multiselect" as const,
            value: selectedStates.map((stateCode) => ({
              label:
                stateOptions.find((opt) => opt.value === stateCode)?.label ||
                stateCode,
              value: stateCode,
            })),
            options: stateOptions,
            onChange: handleStateChange,
          },
          {
            label: "Step 2: Select County(s)",
            value:
              editData?.ba_counties
                ?.filter((county: any) =>
                  selectedStates.includes(county.state_code)
                )
                ?.map((county: any) => ({
                  label: `${county.county}, ${county.state_code}`,
                  value: county.id,
                })) || [],
            field: "ba_counties",
            type: "multiselect",
            options: availableCounties.map((county) => ({
              label: `${county.county}, ${county.state_code}`,
              value: county.id,
            })),
            disabled: selectedStates.length === 0,
            required: false,
          } as FormField,
        ],
      },
      {
        title: "Available Items",
        fields: [
          {
            label: "Available Items",
            value: selectedChecklistItems.map((item) => ({
              label: item.item,
              value: item.id,
            })),
            field: "checklist_items",
            type: "multiselect",
            options: [
              ...checklistItems.map((item) => ({
                label: item.item,
                value: item.id,
              })),
              {
                label: "+ Add Item",
                value: "addItem",
              },
            ],
            onChange: handleChecklistChange,
          } as FormField,
        ],
      },
    ];

    return sections;
  };

  return (
    <>
      <DynamicForm
        fields={getProfessionalFields()}
        initialData={professionalData || {}}
        onEdit={handleEdit}
        section={{
          label: "Professional",
          tableName: "profile_data",
        }}
      />

      <SlidingSidebar
        open={editMode}
        onClose={() => setEditMode(false)}
        width="500px"
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
            <SidebarAvatar
              src={editData?.avatar_url || ""}
              alt={editData?.username || "User avatar"}
              onEditAvatar={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    handleAvatarUpload(file);
                  }
                };
                input.click();
              }}
            />
            <SidebarEditForm
              sections={getProfessionalFormSections()}
              onValueChange={(field: string, value: any) => {
                if (field === "state") {
                  handleStateChange(
                    value as Array<string | { label: string; value: string }>
                  );
                } else if (field === "ba_counties") {
                  // Filter out any values that don't have a valid ID
                  const validCounties = value.filter(
                    (v: any) => v.value != null
                  );

                  // Get all existing counties
                  const allExistingCounties = editData?.ba_counties || [];

                  // Keep counties from other states
                  const countiesFromOtherStates = allExistingCounties.filter(
                    (county: any) => !selectedStates.includes(county.state_code)
                  );

                  // Map the new selections to our county format
                  const newCounties = validCounties.map((v: any) => ({
                    id: v.value,
                    county: v.label.split(",")[0].trim(),
                    state_code: v.label.split(",")[1].trim(),
                  }));

                  // Combine counties from other states with new selections
                  const updatedCounties = [
                    ...countiesFromOtherStates,
                    ...newCounties,
                  ];

                  setEditData((prev: Record<string, any>) => ({
                    ...prev,
                    ba_counties: updatedCounties,
                  }));
                } else if (field === "checklist_items") {
                  // Handle checklist items specially
                  if (value.some((item: any) => item.value === "addItem")) {
                    // Open the dialog to add a new item
                    setDialogOpen(true);

                    // Filter out the "Add Item" option from the selection
                    const actualItems = value.filter(
                      (item: any) => item.value !== "addItem"
                    );

                    // Update the selected items
                    setSelectedChecklistItems(
                      actualItems.map((item: any) => ({
                        id: item.value,
                        item: item.label,
                        available: true,
                      }))
                    );

                    // Update the edit data
                    setEditData((prev: Record<string, any>) => ({
                      ...prev,
                      [field]: actualItems,
                    }));
                  } else {
                    // Normal selection change
                    setSelectedChecklistItems(
                      value.map((item: any) => ({
                        id: item.value,
                        item: item.label,
                        available: true,
                      }))
                    );

                    // Update the edit data
                    setEditData((prev: Record<string, any>) => ({
                      ...prev,
                      [field]: value,
                    }));
                  }
                } else {
                  setEditData((prev: Record<string, any>) => ({
                    ...prev,
                    [field]: value,
                  }));
                }
              }}
            />
          </Box>

          <ButtonControls>
            <Button
              onClick={handleSaveProfessional}
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
            <Button onClick={() => setEditMode(false)} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>

      <Dialog
        open={noCountiesDialogOpen}
        onClose={() => setNoCountiesDialogOpen(false)}
      >
        <DialogTitle>No Coverage Selected</DialogTitle>
        <DialogContent>
          <Typography>
            No coverage selected. You will not be eligible for any bookings. Do
            you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoCountiesDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setNoCountiesDialogOpen(false);
              saveChanges();
            }}
            color="primary"
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add New Checklist Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Checklist Item"
            type="text"
            fullWidth
            variant="outlined"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddItem} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default BASettings;
